import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { DateValue } from '@internationalized/date';
import { BackArrow, Button, CheckBox, DatePickerCustom, Input, Select, TopProgress, Typography } from '../../../../components';
import { billingService, clientService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import { usePageTitle } from '../../../../context/navigationContext';
import { handleStatusName } from '../../../../utils/handle-status-name';
import {
  ActionsRow,
  Container,
  ErrorMessage,
  FieldItem,
  FieldRow,
  FormBody,
  Header,
  ItemCard,
  ItemHeader,
  ItemRow,
  ItemsHeader,
  ItemsSection,
  TitleBlock,
} from './styles';
import { InvoiceStatusContainer, InvoiceStatusText } from '../../invoice-page/styles';
import { parseApiDateToDateValue, dateValueToIsoString } from '../../../../components/date-picker/utils';
import { icons } from '../../../../icons';
import { v4 as uuidv4 } from 'uuid';
import useDebounce from '../../../../hooks/useDebounce';

type InvoiceItemForm = {
  id?: number;
  description: string;
  amount: string;
  item_type: string;
  service: string;
  service_cycle: string;
  taxed: boolean;
  __tempId: string;
};

type InvoiceFormValues = {
  client: number | '';
  status: string;
  issue_date: DateValue | null;
  due_date: DateValue | null;
  fiscal_date: DateValue | null;
  fiscal_due_date: DateValue | null;
  items: InvoiceItemForm[];
};

type SelectOption = {
  id: string | number;
  value: string | number;
  label: string;
};

const DEFAULT_STATUSES = ['unpaid', 'paid', 'canceled', 'refunded', 'inprogress'] as const;

const InvoiceEdit: FC = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const translate = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });

  const isEditMode = Boolean(invoiceId);
  usePageTitle(isEditMode ? t("EDIT_INVOICE") : t("CREATE_INVOICE"));
  const defaultStatuses = useMemo(() => [...DEFAULT_STATUSES], []);

  const [invoice, setInvoice] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [clientSearch, setClientSearch] = useState<string>('');
  const [selectedClientOption, setSelectedClientOption] = useState<SelectOption | null>(null);
  const debouncedClientSearch = useDebounce(clientSearch, 400);
  const lastLoadedSearchRef = useRef<string | null>(null);
  const [itemTypeOptions, setItemTypeOptions] = useState<SelectOption[]>([]);
  const [statusOptionsFromApi, setStatusOptionsFromApi] = useState<SelectOption[]>([]);
  const [servicesByClient, setServicesByClient] = useState<{
    clientId: number | null;
    raw: any[];
    options: SelectOption[];
  }>({ clientId: null, raw: [], options: [] });
  const [servicesLoading, setServicesLoading] = useState<boolean>(false);
  const [serviceCyclesMap, setServiceCyclesMap] = useState<Record<string, SelectOption[]>>({});
  const serviceCyclesLoadingRef:any = useRef<Record<string, Promise<SelectOption[]>>>({});
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<InvoiceFormValues>({
    client: '',
    status: '',
    issue_date: null,
    due_date: null,
    fiscal_date: null,
    fiscal_due_date: null,
    items: [],
  });

  const createClientOption = useCallback(
    (client: any): SelectOption => ({
      id: client.id,
      value: client.id,
      label:
        client.display ||
        client.long_name ||
        client.name ||
        [client.first_name, client.last_name].filter(Boolean).join(' ') ||
        `#${client.id}`,
    }),
    [],
  );

  const loadClients = useCallback(
    async (searchTerm: string, fallbackOption?: SelectOption | null) => {
      try {
        const response = await clientService.getClientList({
          page: 1,
          pageSize: 20,
          ordering: '-id',
          search: searchTerm || '',
        });

        const clientsRaw = response?.data?.objects ?? [];
        const mapped = clientsRaw.map((client: any) => createClientOption(client));

        if (fallbackOption && !mapped.some((option:any) => option.id === fallbackOption.id)) {
          mapped.push(fallbackOption);
        }

        setClientOptions(mapped);
      } catch (error) {
        console.error('Failed to load clients', error);
      }
    },
    [createClientOption],
  );

  const parseItemTypes = useCallback((itemTypesData: any): SelectOption[] => {
    if (!itemTypesData) {
      return [];
    }
    if (Array.isArray(itemTypesData)) {
      return itemTypesData.map((entry: any) => {
        if (typeof entry === 'string') {
          return {
            id: entry,
            value: entry,
            label: entry,
          };
        }
        if (entry && typeof entry === 'object') {
          const value = entry.value ?? entry.id;
          const label = entry.label ?? entry.display ?? entry.display_name ?? value;
          return {
            id: String(value),
            value: String(value),
            label: String(label),
          };
        }
        return {
          id: String(entry),
          value: String(entry),
          label: String(entry),
        };
      });
    }
    if (typeof itemTypesData === 'object') {
      return Object.entries(itemTypesData).map(([value, label]) => ({
        id: String(value),
        value: String(value),
        label: String(label ?? value),
      }));
    }
    return [];
  }, []);

  const parseInvoiceStatuses = useCallback(
    (statusesData: any): SelectOption[] => {
      if (!statusesData) {
        return [];
      }
      if (Array.isArray(statusesData)) {
        return statusesData.map((entry: any) => {
          if (Array.isArray(entry)) {
            const [value, label] = entry;
            const fallbackLabel = handleStatusName(String(value), t);
            return {
              id: String(value),
              value: String(value),
              label: String(label ?? fallbackLabel),
            };
          }
          if (entry && typeof entry === 'object') {
            const value = entry.value ?? entry.id;
            const label = entry.label ?? entry.display_name ?? entry.display ?? handleStatusName(String(value), t);
            return {
              id: String(value),
              value: String(value),
              label: String(label),
            };
          }
          const fallbackLabel = handleStatusName(String(entry), t);
          return {
            id: String(entry),
            value: String(entry),
            label: fallbackLabel,
          };
        });
      }
      if (typeof statusesData === 'object') {
        return Object.entries(statusesData).map(([value, label]) => ({
          id: String(value),
          value: String(value),
          label: String(label ?? handleStatusName(String(value), t)),
        }));
      }
      return [];
    },
    [t],
  );

  const mergeServiceArrays = useCallback((base: any[], extra: any[]) => {
    const map = new Map<string, any>();
    [...base, ...extra].forEach((service: any) => {
      if (!service) {
        return;
      }
      map.set(String(service.id), service);
    });
    return Array.from(map.values());
  }, []);

  const buildServiceOptions = useCallback(
    (services: any[]) =>
      services.map((service: any) => {
        const displayName =
          service.display_name ||
          service.product?.name ||
          service.product?.description ||
          `#${service.id}`;
        const statusLabel = service.status
          ? ` (${handleStatusName(String(service.status), t)})`
          : '';
        return {
          id: String(service.id),
          value: String(service.id),
          label: `${displayName}${statusLabel}`,
        };
      }),
    [t],
  );

  const loadCreateOptions = useCallback(async () => {
    try {
      setOptionsLoading(true);
      const response = await billingService.createOptions();
      const data = response?.data || {};

      setItemTypeOptions(parseItemTypes(data.invoice_item_types));
      setStatusOptionsFromApi(parseInvoiceStatuses(data.invoice_statuses));

      if (Array.isArray(data.services)) {
        const merged = mergeServiceArrays([], data.services);
        setServicesByClient({
          clientId: null,
          raw: merged,
          options: buildServiceOptions(merged),
        });
      } else {
        setServicesByClient({ clientId: null, raw: [], options: [] });
      }
    } catch (error) {
      console.error('Failed to load invoice create options', error);
    } finally {
      setOptionsLoading(false);
    }
  }, [parseItemTypes, parseInvoiceStatuses, mergeServiceArrays, buildServiceOptions]);

  const loadEditOptions = useCallback(
    async (invoiceIdParam: number | string, clientId: number | null) => {
      try {
        setOptionsLoading(true);
        const response = await billingService.editOptions(invoiceIdParam);
        const data = response?.data || {};

        setItemTypeOptions(parseItemTypes(data.invoice_item_types));
        setStatusOptionsFromApi(parseInvoiceStatuses(data.invoice_statuses));

        const servicesArray = Array.isArray(data.services) ? data.services : [];
        const normalizedClientId =
          clientId === null || clientId === undefined ? null : Number(clientId);

        setServicesByClient((prev) => {
          const baseServices =
            normalizedClientId !== null && prev.clientId === normalizedClientId
              ? prev.raw
              : [];
          const merged = mergeServiceArrays(baseServices, servicesArray);
          return {
            clientId: normalizedClientId,
            raw: merged,
            options: buildServiceOptions(merged),
          };
        });
      } catch (error) {
        console.error('Failed to load invoice edit options', error);
      } finally {
        setOptionsLoading(false);
      }
    },
    [parseItemTypes, parseInvoiceStatuses, mergeServiceArrays, buildServiceOptions],
  );

  const loadServicesForClient = useCallback(
    async (clientIdValue: number | string | null) => {
      if (clientIdValue === null || clientIdValue === undefined || clientIdValue === '') {
        setServicesByClient({ clientId: null, raw: [], options: [] });
        return [] as SelectOption[];
      }

      const numericClientId = Number(clientIdValue);
      if (Number.isNaN(numericClientId)) {
        return [] as SelectOption[];
      }

      setServicesLoading(true);
      try {
        const response = await billingService.getServicesByClient(numericClientId);
        const servicesRaw = response?.data?.objects ?? [];
        let computedOptions: SelectOption[] = [];
        setServicesByClient((prev) => {
          const baseServices = prev.clientId === numericClientId ? prev.raw : [];
          const merged = mergeServiceArrays(baseServices, servicesRaw);
          computedOptions = buildServiceOptions(merged);
          return {
            clientId: numericClientId,
            raw: merged,
            options: computedOptions,
          };
        });
        return computedOptions;
      } catch (error) {
        console.error('Failed to load services for client', error);
        setServicesByClient((prev) =>
          prev.clientId === numericClientId ? prev : { clientId: numericClientId, raw: [], options: [] },
        );
        return [] as SelectOption[];
      } finally {
        setServicesLoading(false);
      }
    },
    [buildServiceOptions, mergeServiceArrays],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!isEditMode) {
        const blankItem: InvoiceItemForm = {
          description: '',
          amount: '',
          item_type: '',
          service: '',
          service_cycle: '',
          taxed: false,
          __tempId: uuidv4(),
        };

        setInvoice(null);
        setSelectedClientOption(null);
        setClientOptions([]);
        setItemTypeOptions([]);
        setStatusOptionsFromApi([]);
        setServicesByClient({ clientId: null, raw: [], options: [] });
        setServiceCyclesMap({});
        serviceCyclesLoadingRef.current = {};
        setInitialValues({
          client: '',
          status: defaultStatuses[0],
          issue_date: null,
          due_date: null,
          fiscal_date: null,
          fiscal_due_date: null,
          items: [blankItem],
        });
        lastLoadedSearchRef.current = '';
        await Promise.all([loadCreateOptions(), loadClients('', null)]);
        setPageLoading(false);
        return;
      }

      if (!invoiceId) {
        setPageLoading(false);
        return;
      }

      try {
        const invoiceRes = await billingService.getInvoice(invoiceId);
        const invoiceData = invoiceRes.data;
        setInvoice(invoiceData);

        const invoiceClientOption = invoiceData?.client
          ? createClientOption(invoiceData.client)
          : null;

        if (invoiceClientOption) {
          setSelectedClientOption(invoiceClientOption);
          setClientOptions([invoiceClientOption]);
        } else {
          setSelectedClientOption(null);
          setClientOptions([]);
        }
        setClientSearch('');

        setInitialValues({
          client: invoiceData.client?.id ?? '',
          status: invoiceData.status ?? '',
          issue_date: parseApiDateToDateValue(invoiceData.issue_date),
          due_date: parseApiDateToDateValue(invoiceData.due_date),
          fiscal_date: parseApiDateToDateValue(invoiceData.fiscal_date),
          fiscal_due_date: parseApiDateToDateValue(invoiceData.fiscal_due_date),
          items: (invoiceData.items || []).map((item: any) => ({
            id: item.id,
            description: item.description || '',
            amount:
              item.amount !== undefined && item.amount !== null
                ? String(item.amount)
                : '',
            item_type: item.item_type || '',
            service:
              item.service !== undefined && item.service !== null
                ? String(item.service)
                : '',
            service_cycle:
              item.service_cycle !== undefined && item.service_cycle !== null
                ? String(item.service_cycle)
                : '',
            taxed: Boolean(item.taxed),
            __tempId: uuidv4(),
          })),
        });

        await loadClients('', invoiceClientOption);
        const clientId = invoiceData.client?.id ?? null;
        await loadEditOptions(invoiceId, clientId);
        if (clientId !== null && clientId !== undefined) {
          await loadServicesForClient(clientId);
        }
        lastLoadedSearchRef.current = '';
      } catch (error: any) {
        if (error?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: error.response.data.detail,
          });
        } else {
          console.error(error);
        }
        navigate(-1);
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [
    invoiceId,
    isEditMode,
    loadCreateOptions,
    loadClients,
    defaultStatuses,
    loadEditOptions,
    loadServicesForClient,
    createClientOption,
    viewAlert,
    navigate,
  ]);

  const loadServiceCycles = useCallback(
    async (serviceId: string | number) => {
      if (!serviceId && serviceId !== 0) {
        return [] as SelectOption[];
      }
      const key = String(serviceId);
      if (serviceCyclesMap[key]) {
        return serviceCyclesMap[key];
      }
      if (serviceCyclesLoadingRef.current[key]) {
        return serviceCyclesLoadingRef.current[key];
      }

      const promise = (async () => {
        try {
          const numericServiceId = Number(serviceId);
          if (Number.isNaN(numericServiceId)) {
            return [] as SelectOption[];
          }
          const response = await billingService.createServiceCycles(numericServiceId);
          const cyclesRaw = response?.data?.objects ?? [];
          const mapped = cyclesRaw.map((cycle: any) => {
            const dateLabel = cycle.end_display
              ? `${cycle.start_display} → ${cycle.end_display}`
              : cycle.start_display || String(cycle.id);
            const priceLabel = cycle.price
              ? ` • ${cycle.price}${cycle.currency ? ` ${cycle.currency}` : ''}`
              : '';
            return {
              id: String(cycle.id),
              value: String(cycle.id),
              label: `${dateLabel}${priceLabel}`,
            };
          });
          setServiceCyclesMap((prev) => ({
            ...prev,
            [key]: mapped,
          }));
          return mapped as SelectOption[];
        } catch (error) {
          console.error('Failed to load service cycles', error);
          return [] as SelectOption[];
        } finally {
          delete serviceCyclesLoadingRef.current[key];
        }
      })();

      serviceCyclesLoadingRef.current[key] = promise;
      return promise;
    },
    [serviceCyclesMap],
  );

  useEffect(() => {
    const trimmedSearch = debouncedClientSearch.trim();
    if (lastLoadedSearchRef.current === trimmedSearch) {
      return;
    }

    const ensureOption = selectedClientOption || (invoice?.client ? createClientOption(invoice.client) : null);

    loadClients(trimmedSearch, ensureOption).finally(() => {
      lastLoadedSearchRef.current = trimmedSearch;
    });
  }, [debouncedClientSearch, loadClients, selectedClientOption, invoice, createClientOption]);

  const isFiscalInvoice = Boolean(invoice?.is_fiscal);

  const statusOptions = useMemo<SelectOption[]>(() => {
    if (statusOptionsFromApi.length > 0) {
      return statusOptionsFromApi.map((statusOption) => ({
        ...statusOption,
        label: statusOption.label || handleStatusName(String(statusOption.value), t),
      }));
    }

    const statusesSource =
      Array.isArray(invoice?.statuses) && invoice.statuses.length > 0
        ? invoice.statuses
        : invoice?.status
        ? [invoice.status]
        : defaultStatuses;
    return statusesSource.map((status: string) => ({
      id: String(status),
      value: String(status),
      label: handleStatusName(String(status), t),
    }));
  }, [statusOptionsFromApi, invoice, t, defaultStatuses]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        client: yup
          .number()
          .typeError(`${t('REQUIRED_FIELD')}`)
          .required(`${t('REQUIRED_FIELD')}`),
        status: yup.string().required(`${t('REQUIRED_FIELD')}`),
        issue_date: yup
          .mixed<DateValue | null>()
          .nullable()
          .required(`${t('REQUIRED_FIELD')}`),
        due_date: yup
          .mixed<DateValue | null>()
          .nullable()
          .required(`${t('REQUIRED_FIELD')}`),
        fiscal_date: (isFiscalInvoice
          ? yup
              .mixed<DateValue | null>()
              .nullable()
              .required(`${t('REQUIRED_FIELD')}`)
          : yup.mixed<DateValue | null>().nullable()),
        fiscal_due_date: (isFiscalInvoice
          ? yup
              .mixed<DateValue | null>()
              .nullable()
              .required(`${t('REQUIRED_FIELD')}`)
          : yup.mixed<DateValue | null>().nullable()) ,
        items: yup
          .array()
          .of(
            yup.object({
              id: yup.mixed(),
              __tempId: yup.string(),
              description: yup.string().required(`${t('REQUIRED_FIELD')}`),
              amount: yup
                .string()
                .required(`${t('REQUIRED_FIELD')}`)
                .test('is-number', `${t('REQUIRED_FIELD')}`, (value) => {
                  if (value === undefined || value === null || value === '') {
                    return false;
                  }
                  return !Number.isNaN(Number(value));
                }),
              item_type: yup.string().required(`${t('REQUIRED_FIELD')}`),
              service: yup.string().nullable(),
              service_cycle: yup.string().nullable(),
              taxed: yup.boolean(),
            }),
          )
          .min(1, `${t('REQUIRED_FIELD')}`)
          .required(`${t('REQUIRED_FIELD')}`),
      }),
    [isFiscalInvoice, t],
  );

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = useFormik<InvoiceFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (formValues) => {
      if (!invoiceId && isEditMode) {
        return;
      }
      setSubmitLoading(true);

      const getDatePayload = (
        value: DateValue | null,
        fallback?: string | null,
      ) => {
        const iso = dateValueToIsoString(value);
        if (iso) {
          return iso;
        }
        return fallback ?? null;
      };

      try {
        const payloadBase = {
          client: Number(formValues.client),
          status: formValues.status,
          issue_date: getDatePayload(formValues.issue_date, invoice?.issue_date),
          due_date: getDatePayload(formValues.due_date, invoice?.due_date),
          fiscal_date: getDatePayload(
            formValues.fiscal_date,
            invoice?.fiscal_date,
          ),
          fiscal_due_date: getDatePayload(
            formValues.fiscal_due_date,
            invoice?.fiscal_due_date,
          ),
          items: formValues.items.map((item) => ({
            id: item.id,
            description: item.description,
            amount: item.amount.trim(),
            item_type: item.item_type,
            service:
              item.service !== ''
                ? Number.isNaN(Number(item.service))
                  ? item.service
                  : Number(item.service)
                : null,
            service_cycle:
              item.service_cycle !== ''
                ? Number.isNaN(Number(item.service_cycle))
                  ? item.service_cycle
                  : Number(item.service_cycle)
                : null,
            taxed: Boolean(item.taxed),
          })),
        };

        if (isEditMode) {
          await billingService.editInvoice(invoiceId!, {
            id: invoice?.id,
            ...payloadBase,
          });
          await viewAlert({
            severity: 'success',
            message: translate(
              'INVOICE_UPDATED_SUCCESSFULLY',
              'Invoice updated successfully',
            ),
          });
          navigate(`/billing/invoices/${invoiceId}`);
        } else {
          const response = await billingService.createInvoice(payloadBase);
          await viewAlert({
            severity: 'success',
            message: translate('INVOICE_CREATED_SUCCESSFULLY', 'Invoice created successfully'),
          });
          const newInvoiceId = response?.data?.id || response?.data?.invoice?.id;
          if (newInvoiceId) {
            navigate(`/billing/invoices/${newInvoiceId}`);
          } else {
            navigate('/billing/invoices');
          }
        }
      } catch (error: any) {
        if (error?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: error.response.data.detail,
          });
        } else {
          console.error(error);
          viewAlert({
            severity: 'error',
            message: 'Failed to update invoice',
          });
        }
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  const handleClientSelect = async (selected: number | string) => {
    if (selected === '' || selected === null || selected === undefined) {
      return;
    }

    const numericId = typeof selected === 'string' ? Number(selected) : selected;
    if (Number.isNaN(numericId)) {
      return;
    }

    setFieldValue('client', numericId);
    setClientSearch('');
    lastLoadedSearchRef.current = '';

    const optionFromList = clientOptions.find(
      (option) => Number(option.id) === Number(numericId),
    );

    if (optionFromList) {
      setSelectedClientOption(optionFromList);
      await loadClients('', optionFromList);
    } else if (invoice?.client && invoice.client.id === numericId) {
      const invoiceClient = createClientOption(invoice.client);
      setSelectedClientOption(invoiceClient);
      await loadClients('', invoiceClient);
    } else {
      setSelectedClientOption(null);
      await loadClients('', null);
    }

    setServiceCyclesMap({});
    serviceCyclesLoadingRef.current = {};
    await loadServicesForClient(numericId);
    values.items.forEach((_, idx) => {
      setFieldValue(`items[${idx}].service`, '');
      setFieldValue(`items[${idx}].service_cycle`, '');
    });
  };

  const handleItemChange = useCallback(
    (index: number, field: keyof InvoiceItemForm, value: any) => {
      setFieldValue(`items[${index}].${field}`, value);
    },
    [setFieldValue],
  );

  useEffect(() => {
    if (
      values.client === '' ||
      values.client === null ||
      values.client === undefined
    ) {
      return;
    }
    const option = clientOptions.find(
      (item) => Number(item.id) === Number(values.client),
    );
    if (option && (!selectedClientOption || option.id !== selectedClientOption.id)) {
      setSelectedClientOption(option);
    }
  }, [clientOptions, selectedClientOption, values.client]);

  useEffect(() => {
    if (statusOptionsFromApi.length === 0) {
      return;
    }
    if (!statusOptionsFromApi.some((option) => option.value === values.status)) {
      setFieldValue('status', statusOptionsFromApi[0].value);
    }
  }, [statusOptionsFromApi, setFieldValue, values.status]);

  useEffect(() => {
    if (itemTypeOptions.length === 0) {
      return;
    }
    values.items.forEach((item, idx) => {
      if (!item.item_type) {
        setFieldValue(`items[${idx}].item_type`, itemTypeOptions[0].value);
      }
    });
  }, [itemTypeOptions, setFieldValue, values.items]);

  useEffect(() => {
    const clientId = values.client;
    if (!clientId) {
      return;
    }
    const numericClientId = Number(clientId);
    if (Number.isNaN(numericClientId)) {
      return;
    }
    if (servicesByClient.clientId !== numericClientId) {
      return;
    }

    const allowedIds = new Set(
      servicesByClient.raw.map((service: any) => String(service.id)),
    );
    values.items.forEach((item, idx) => {
      if (item.service && !allowedIds.has(String(item.service))) {
        setFieldValue(`items[${idx}].service`, '');
        setFieldValue(`items[${idx}].service_cycle`, '');
      }
    });
  }, [servicesByClient, setFieldValue, values.items, values.client]);

  useEffect(() => {
    values.items.forEach((item) => {
      if (item.service) {
        const key = String(item.service);
        if (!serviceCyclesMap[key]) {
          loadServiceCycles(item.service);
        }
      }
    });
  }, [values.items, serviceCyclesMap, loadServiceCycles]);

  const handleItemServiceChange = useCallback(
    async (index: number, serviceId: string | number) => {
      const serviceValue = serviceId ? String(serviceId) : '';
      handleItemChange(index, 'service', serviceValue);
      handleItemChange(index, 'service_cycle', '');

      const cycles = await loadServiceCycles(serviceValue);
      if (cycles.length > 0) {
        setFieldValue(`items[${index}].service_cycle`, cycles[0].value);
      }
    },
    [handleItemChange, loadServiceCycles, setFieldValue],
  );

  const addItem = () => {
    const newItem: InvoiceItemForm = {
      description: '',
      amount: '',
      item_type: '',
      service: '',
      service_cycle: '',
      taxed: false,
      __tempId: uuidv4(),
    };
    setFieldValue('items', [...values.items, newItem]);
  };

  const removeItem = (index: number) => {
    const updatedItems = values.items.filter((_, idx) => idx !== index);
    setFieldValue('items', updatedItems);
  };

  const getItemFieldError = (
    index: number,
    field: keyof InvoiceItemForm,
  ): string | undefined => {
    const itemsErrors = errors.items;
    const itemsTouched = touched.items;
    if (
      Array.isArray(itemsErrors) &&
      Array.isArray(itemsTouched) &&
      itemsErrors[index] &&
      itemsTouched[index]
    ) {
      const fieldError = (itemsErrors[index] as any)?.[field];
      const fieldTouched = (itemsTouched[index] as any)?.[field];
      if (fieldTouched && fieldError) {
        return fieldError as string;
      }
    }
    return undefined;
  };

  const serviceSelectOptions = useMemo(() => {
    const clientId = values.client;
    if (!clientId) {
      return [] as SelectOption[];
    }
    const numericClientId = Number(clientId);
    if (Number.isNaN(numericClientId)) {
      return [] as SelectOption[];
    }
    if (servicesByClient.clientId !== numericClientId) {
      return [] as SelectOption[];
    }
    return servicesByClient.options;
  }, [servicesByClient, values.client]);

  if (pageLoading) {
    return <TopProgress loading={pageLoading} />;
  }

  return (
    <Container>
      <BackArrow fallbackPath="/billing/invoices" />
      <Header>
        <TitleBlock>
          <Typography variant="h2">
            {isEditMode
              ? `${translate('EDIT_INVOICE', 'Edit invoice')} ${invoice?.display_number || invoice?.id || ''}`
              : translate('CREATE_INVOICE', 'Create invoice')}
          </Typography>
          {isEditMode && invoice?.client?.display ? (
            <Typography variant="body">
              {invoice.client.display}
            </Typography>
          ) : null}
        </TitleBlock>
        {isEditMode ? (
          <InvoiceStatusContainer status={invoice?.status}>
            <InvoiceStatusText status={invoice?.status}>
              {handleStatusName(invoice?.status, t)}
            </InvoiceStatusText>
          </InvoiceStatusContainer>
        ) : null}
      </Header>
      <FormBody>
        <FieldRow>
          <FieldItem>
            <Select
              required
              title={translate('CLIENT', 'Client')}
              data={clientOptions}
              selectedValue={values.client}
              fieldName="client"
              onChange={handleClientSelect}
              hasSearch
              onSearchChange={setClientSearch}
              customStyles={{ width: '100%' }}
              toolTip={false}
              errors={errors}
              touched={touched}
            />
          </FieldItem>
          <FieldItem>
            <Select
              required
              title={translate('STATUS', 'Status')}
              data={statusOptions}
              selectedValue={values.status}
              fieldName="status"
              onChange={(selected: string) => setFieldValue('status', selected)}
              customStyles={{ width: '100%' }}
              toolTip={false}
              errors={errors}
              touched={touched}
            />
          </FieldItem>
        </FieldRow>
        <FieldRow>
          <FieldItem>
            <DatePickerCustom
              required
              title={translate('ISSUE_DATE', 'Issue date')}
              value={values.issue_date}
              onChange={(nextValue) => setFieldValue('issue_date', nextValue)}
              hideTimeZone
              showMonthAndYearPickers
            />
            {touched.issue_date && errors.issue_date ? (
              <ErrorMessage>{errors.issue_date as string}</ErrorMessage>
            ) : null}
          </FieldItem>
          <FieldItem>
            <DatePickerCustom
              required
              title={translate('DUE_DATE', 'Due date')}
              value={values.due_date}
              onChange={(nextValue) => setFieldValue('due_date', nextValue)}
              hideTimeZone
              showMonthAndYearPickers
            />
            {touched.due_date && errors.due_date ? (
              <ErrorMessage>{errors.due_date as string}</ErrorMessage>
            ) : null}
          </FieldItem>
          <FieldItem>
            <DatePickerCustom
              required={isFiscalInvoice}
              title={translate('FISCAL_DATE', 'Fiscal date')}
              value={values.fiscal_date}
              onChange={(nextValue) => setFieldValue('fiscal_date', nextValue)}
              hideTimeZone
              showMonthAndYearPickers
            />
            {touched.fiscal_date && errors.fiscal_date ? (
              <ErrorMessage>{errors.fiscal_date as string}</ErrorMessage>
            ) : null}
          </FieldItem>
          <FieldItem>
            <DatePickerCustom
              required={isFiscalInvoice}
              title={translate('FISCAL_DUE_DATE', 'Fiscal due date')}
              value={values.fiscal_due_date}
              onChange={(nextValue) =>
                setFieldValue('fiscal_due_date', nextValue)
              }
              hideTimeZone
              showMonthAndYearPickers
            />
            {touched.fiscal_due_date && errors.fiscal_due_date ? (
              <ErrorMessage>{errors.fiscal_due_date as string}</ErrorMessage>
            ) : null}
          </FieldItem>
        </FieldRow>

        <ItemsSection>
          <ItemsHeader>
            <Typography variant="h3">
              {translate('INVOICE_ITEMS', 'Invoice items')}
            </Typography>
            <Button
              variant="stroke"
              size="small"
              icon={icons.PlusDefault}
              onClick={addItem}
              title={translate('ADD', 'Add')}
            />
          </ItemsHeader>
          {values.items.map((item, index) => (
            <ItemCard key={item.id ?? item.__tempId}>
              <ItemHeader>
                <Typography variant="h3">
                  {translate('ITEM', 'Item')} #{index + 1}
                </Typography>
                <Button
                  variant="stroke"
                  size="small"
                  icon={icons.DeleteIcon}
                  onClick={() => removeItem(index)}
                  title={translate('DELETE', 'Delete')}
                />
              </ItemHeader>
              <ItemRow>
                <Input
                  required
                  isTextArea
                  title={translate('DESCRIPTION', 'Description')}
                  placeholder=""
                  fieldName={`items[${index}].description`}
                  value={item.description}
                  customStyles={{minHeight:'140px'}}
                  handleChange={(e: any) =>
                    handleItemChange(index, 'description', e.target.value)
                  }
                  handleBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                  arrayFields
                  arrayName="items"
                  arrayIndex={index}
                  arrayFieldName="description"
                  values={values}
                />
              </ItemRow>
              <ItemRow>
                <FieldItem>
                  <Select
                    required
                    title={translate('ITEM_TYPE', 'Item type')}
                    data={itemTypeOptions}
                    selectedValue={item.item_type}
                    fieldName="item_type"
                    onChange={(selected: string) =>
                      handleItemChange(index, 'item_type', String(selected))
                    }
                    hasSearch
                    customStyles={{ width: '100%' }}
                    toolTip={false}
                    errors={errors}
                    touched={touched}
                  />
                </FieldItem>
                <FieldItem>
                  <Input
                    required
                    title={translate('AMOUNT', 'Amount')}
                    placeholder=""
                    fieldName={`items[${index}].amount`}
                    type="number"
                    value={item.amount}
                    handleChange={(e: any) =>
                      handleItemChange(index, 'amount', e.target.value)
                    }
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    arrayFields
                    arrayName="items"
                    arrayIndex={index}
                    arrayFieldName="amount"
                    values={values}
                  />
                </FieldItem>
                <FieldItem>
                  <Select
                    title={translate('SERVICE', 'Service')}
                    data={serviceSelectOptions}
                    selectedValue={item.service}
                    fieldName="service"
                    onChange={(selected: string | number) =>
                      handleItemServiceChange(index, selected)
                    }
                    hasSearch
                    customStyles={{ width: '100%' }}
                    toolTip={false}
                    disabled={
                      !values.client ||
                      optionsLoading ||
                      servicesLoading ||
                      servicesByClient.clientId !== Number(values.client) ||
                      serviceSelectOptions.length === 0
                    }
                    errors={errors}
                    touched={touched}
                  />
                </FieldItem>
                <FieldItem>
                  <Select
                    title={translate('SERVICE_CYCLE', 'Service cycle')}
                    data={serviceCyclesMap[String(item.service)] || []}
                    selectedValue={item.service_cycle}
                    fieldName="service_cycle"
                    onChange={(selected: string | number) =>
                      handleItemChange(index, 'service_cycle', String(selected))
                    }
                    hasSearch
                    customStyles={{ width: '100%' }}
                    toolTip={false}
                    disabled={
                      !item.service ||
                      !values.client ||
                      servicesByClient.clientId !== Number(values.client) ||
                      optionsLoading ||
                      servicesLoading ||
                      (serviceCyclesMap[String(item.service)] || []).length === 0
                    }
                    errors={errors}
                    touched={touched}
                  />
                </FieldItem>
              </ItemRow>
              <CheckBox
                title={translate('TAXED', 'Taxed')}
                value={item.taxed}
                onChange={() =>
                  handleItemChange(index, 'taxed', !item.taxed)
                }
                customStyles={{ marginTop: '4px' }}
              />
              {getItemFieldError(index, 'description') ? (
                <ErrorMessage>
                  {getItemFieldError(index, 'description')}
                </ErrorMessage>
              ) : null}
              {getItemFieldError(index, 'amount') ? (
                <ErrorMessage>{getItemFieldError(index, 'amount')}</ErrorMessage>
              ) : null}
              {getItemFieldError(index, 'item_type') ? (
                <ErrorMessage>
                  {getItemFieldError(index, 'item_type')}
                </ErrorMessage>
              ) : null}
            </ItemCard>
          ))}
          {typeof errors.items === 'string' ? (
            <ErrorMessage>{errors.items}</ErrorMessage>
          ) : null}
        </ItemsSection>

        <ActionsRow>
          <Button
            variant="primary"
            size="display"
            onClick={handleSubmit}
            loading={submitLoading}
            title={isEditMode ? translate('SAVE', 'Save') : translate('CREATE', 'Create')}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate(-1)}
            title={translate('CANCEL', 'Cancel')}
          />
        </ActionsRow>
      </FormBody>
    </Container>
  );
};

export default InvoiceEdit;
